import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface PaginationControlsProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Navigation button children (e.g., NavButton components) */
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled PaginationControls component.
 *
 * A flex container for navigation buttons. Accepts children to allow
 * composition with any navigation button components (NavButton, custom icons, etc.).
 *
 * @example
 * ```tsx
 * <PaginationControls>
 *   <NavButton direction="prev" onClick={handlePrev} disabled={!hasPrev} />
 *   <NavButton direction="next" onClick={handleNext} disabled={!hasNext} />
 * </PaginationControls>
 * ```
 */
const PaginationControls = React.memo(
  ({ children, className, style, ...props }: PaginationControlsProps) => {
    const [css] = useStyletron()

    const containerClassName = css(getContainerStyles())

    return (
      <div
        className={`${containerClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </div>
    )
  }
)

PaginationControls.displayName = 'PaginationControls'

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

const getContainerStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

export default PaginationControls
